import React, { useEffect, useState } from 'react';
import { SaveIcon } from '@heroicons/react/solid';
import { Tooltip } from '@heroui/react';

interface DraftSaveIndicatorProps {
  isSaving: boolean;
  lastSaved: Date | null;
  onManualSave?: () => void;
  manualSaveTriggered?: boolean;
}

const DraftSaveIndicator: React.FC<DraftSaveIndicatorProps> = ({ 
  isSaving, 
  lastSaved, 
  onManualSave, 
  manualSaveTriggered = false 
}) => {
  const [displayText, setDisplayText] = useState<string>('');
  const [showManualSaveMessage, setShowManualSaveMessage] = useState<boolean>(false);
  
  // Calculate time since last save
  const getTimeDisplay = (lastSaved: Date): string => {
    const now = new Date();
    const diffSeconds = Math.floor((now.getTime() - lastSaved.getTime()) / 1000);

    if (diffSeconds < 60) {
      return 'przed chwilą';
    } else if (diffSeconds < 3600) {
      const minutes = Math.floor(diffSeconds / 60);
      return `${minutes} ${minutes === 1 ? 'minutę' : minutes < 5 ? 'minuty' : 'minut'} temu`;
    } else {
      return `o ${lastSaved.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}`;
    }
  };
  
  // Update time display every 30 seconds
  useEffect(() => {
    if (!lastSaved) return;
    
    // Set initial display
    setDisplayText(getTimeDisplay(lastSaved));
    
    // Update periodically
    const interval = setInterval(() => {
      setDisplayText(getTimeDisplay(lastSaved));
    }, 30000); // 30 seconds
    
    return () => clearInterval(interval);
  }, [lastSaved]);

  // Show manual save message for 3 seconds when triggered
  useEffect(() => {
    if (manualSaveTriggered) {
      setShowManualSaveMessage(true);
      const timer = setTimeout(() => {
        setShowManualSaveMessage(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [manualSaveTriggered]);

  // Handle save icon click
  const handleSaveClick = () => {
    if (onManualSave && !isSaving) {
      onManualSave();
    }
  };

  return (
    <div className="flex items-center text-sm">
      {isSaving ? (
        <div className="flex items-center text-gray-500">
          <div className="h-4 w-4 mr-2 rounded-full border-2 border-gray-200 border-t-indigo-500 animate-spin"></div>
          Auto-zapisywanie...
        </div>
      ) : showManualSaveMessage ? (
        <div className="flex items-center text-green-600">
          <span>Szkic zapisany ręcznie!</span>
        </div>
      ) : lastSaved ? (
        <div className="flex items-center text-green-600">
          <Tooltip content={onManualSave ? "Kliknij, aby zapisać ręcznie" : "Zapisywanie po zmianie co 10 sekund"} position="top">
            <SaveIcon 
              className={`h-5 w-5 mr-1 ${onManualSave ? 'cursor-pointer hover:text-green-800' : ''}`} 
              onClick={handleSaveClick}
            />
          </Tooltip>
          <span>Szkic zapisany {displayText}</span>
        </div>
      ) : null}
    </div>
  );
};

export default DraftSaveIndicator;